import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useRef, useState } from 'react'
import { CalibrationMutators } from '~/mutators/calibration'
import { queryClient, useTRPC, useTRPCClient } from '~/services/trpc'

import { Button, Heading1, Heading2, Icon, MagicText } from '@hap/components'

import { InstructionField } from './-components/instruction-field'
import { TrainingList } from './-components/training-list'

interface EvaluationResult {
  interactionId: string
  score: number | null
  reasoning: string
  status: string
}

export const Route = createFileRoute(
  '/_authenticated/$alias/_app/scorecard/$scorecardId/edit/$criteriaId',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const params = Route.useParams()
  const trpc = useTRPC()
  const trpcClient = useTRPCClient()

  const { data: criteria } = useQuery(
    trpc.scorecardCriteria.getAssignmentById.queryOptions(params.criteriaId),
  )

  const { data: seed } = useQuery(
    trpc.seeds.getOrCreateByScorecardId.queryOptions(params.scorecardId),
  )

  const { data: latestRun } = useQuery(
    trpc.calibration.getLatestRun.queryOptions(
      { criteriaAssignmentId: params.criteriaId },
      { enabled: !!params.criteriaId },
    ),
  )

  const [isEvaluating, setIsEvaluating] = useState(false)
  const [results, setResults] = useState<Record<string, EvaluationResult>>({})
  const resultsRef = useRef<Record<string, EvaluationResult>>({})
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null)

  const [refineState, setRefineState] = useState<{
    status: 'idle' | 'analyzing' | 'synthesizing' | 'complete' | 'error'
  }>({ status: 'idle' })
  const refineSubscriptionRef = useRef<{ unsubscribe: () => void } | null>(null)

  const { start: startCalibration, isPending: isStartingCalibration } =
    CalibrationMutators.useStart({
      criteriaAssignmentId: params.criteriaId,
    })

  const startEvaluation = useCallback(async () => {
    if (!seed) return

    // Create calibration run
    const { calibrationRunId } = await startCalibration({
      scorecardId: params.scorecardId,
      seedId: seed.id,
    })

    setIsEvaluating(true)
    setResults({})
    resultsRef.current = {}
    setRefineState({ status: 'idle' })

    subscriptionRef.current = trpcClient.evaluate.criteria.subscribe(
      { criteriaId: params.criteriaId, calibrationRunId },
      {
        onData: (data) => {
          setResults((prev) => {
            const next = { ...prev, [data.interactionId]: data }
            resultsRef.current = next
            return next
          })
        },
        onError: (err) => {
          console.error('Evaluation error:', err)
          setIsEvaluating(false)
          queryClient.invalidateQueries({
            queryKey: trpc.calibration.getLatestRun.queryKey({
              criteriaAssignmentId: params.criteriaId,
            }),
          })
        },
        onComplete: () => {
          setIsEvaluating(false)
          queryClient.invalidateQueries({
            queryKey: trpc.calibration.getLatestRun.queryKey({
              criteriaAssignmentId: params.criteriaId,
            }),
          })
        },
      },
    )
  }, [params.criteriaId, params.scorecardId, seed, startCalibration, trpcClient])

  const startRefinement = useCallback(() => {
    if (!latestRun?.run.id) return

    setRefineState({ status: 'analyzing' })

    refineSubscriptionRef.current = trpcClient.evaluate.refineCriteria.subscribe(
      { criteriaId: params.criteriaId, calibrationRunId: latestRun.run.id },
      {
        onData: (data) => {
          if (data.status === 'analyzing') {
            setRefineState({ status: 'analyzing' })
          } else if (data.status === 'synthesizing') {
            setRefineState({ status: 'synthesizing' })
          } else if (data.status === 'complete') {
            setRefineState({ status: 'complete' })
          }
        },
        onError: (err) => {
          console.error('Refine error:', err)
          setRefineState({ status: 'error' })
        },
        onComplete: () => {
          // No-op; state already updated via onData
        },
      },
    )
  }, [latestRun?.run.id, params.criteriaId, trpcClient])

  const stopEvaluation = useCallback(() => {
    subscriptionRef.current?.unsubscribe()
    subscriptionRef.current = null
    refineSubscriptionRef.current?.unsubscribe()
    refineSubscriptionRef.current = null
    setIsEvaluating(false)
    setRefineState({ status: 'idle' })
  }, [])

  const completedCount = Object.values(results).filter(
    (r) => r.status === 'complete' || r.status === 'error',
  ).length
  const totalCount = latestRun?.run.totalInteractions ?? 0

  const evaluationComplete =
    !isEvaluating && totalCount > 0 && completedCount >= totalCount

  return (
    <div className="bg-accent mt-4 mr-4 max-h-[calc(100vh-73px)] overflow-y-auto rounded-xl">
      <div className="border-background border-b-2 p-4">
        <Heading1>
          <MagicText initialValue={criteria?.criterion.name} onCommit={() => {}} />
        </Heading1>

        {(criteria?.definition ?? criteria?.criterion.defaultDefinition) ? (
          <InstructionField
            value={criteria?.definition ?? criteria?.criterion.defaultDefinition}
          />
        ) : null}

        {/*<Multiline
          value={criteria?.definition ?? criteria?.criterion.defaultDefinition}
        />*/}
        {/*<div>Point System</div>
        <div>Weight</div>*/}
      </div>
      <div className="bg-accent mt-4 rounded-xl p-4">
        <div className="mb-4 flex items-center justify-between">
          {/*<Heading2 className="flex items-center gap-2">
            <Icon name="graduation-cap" weight="fill" />
            Train
          </Heading2>*/}
          <div className="flex items-center gap-2">
            <Button
              variant="lime"
              disabled={isEvaluating || isStartingCalibration || !seed}
              onClick={startEvaluation}
            >
              {isStartingCalibration
                ? 'Starting...'
                : isEvaluating
                  ? 'Evaluating...'
                  : 'Calibrate Criteria'}
            </Button>
            {isEvaluating && (
              <Button variant="ghost" onClick={stopEvaluation}>
                Stop
              </Button>
            )}
            {isEvaluating && totalCount > 0 && (
              <span className="text-muted-foreground text-sm">
                {completedCount} / {totalCount}
              </span>
            )}
            {evaluationComplete && refineState.status === 'idle' && (
              <Button variant="lime" onClick={startRefinement}>
                Refine Criteria
              </Button>
            )}
            {refineState.status === 'analyzing' && (
              <span className="inline-flex items-center gap-1 text-sm">
                <Icon name="arrows-clockwise" className="size-3 animate-spin" />
                Analyzing...
              </span>
            )}
            {refineState.status === 'synthesizing' && (
              <span className="inline-flex items-center gap-1 text-sm">
                <Icon name="arrows-clockwise" className="size-3 animate-spin" />
                Synthesizing...
              </span>
            )}
          </div>
        </div>

        {isEvaluating && totalCount > 0 && (
          <div className="bg-foreground/10 mb-4 h-2 w-full overflow-hidden rounded-full">
            <div
              className="bg-lime-green h-full transition-all duration-300"
              style={{
                width: `${(completedCount / totalCount) * 100}%`,
              }}
            />
          </div>
        )}

        {!!criteria && !!seed && (
          <TrainingList
            criteriaAssignmentId={params.criteriaId}
            scale={criteria.criterion.scale}
            seedId={seed.id}
            calibrationRunId={latestRun?.run.id}
            streamingResults={results}
            isEvaluating={isEvaluating}
          />
        )}

        {refineState.status === 'complete' && latestRun?.run.suggestions && (
          <div className="border-foreground/50 mt-6 rounded border p-4">
            <Heading2>Suggested Improvements</Heading2>
            <div className="mt-4 space-y-4">
              <div>
                <h3 className="text-sm font-semibold">Summary</h3>
                <p className="text-muted-foreground text-sm">
                  {latestRun.run.suggestions.summary}
                </p>
              </div>
              {latestRun.run.suggestions.rationale.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold">Rationale</h3>
                  <ul className="text-muted-foreground list-disc pl-5 text-sm">
                    {latestRun.run.suggestions.rationale.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <h3 className="text-sm font-semibold">Improved Definition</h3>
                <p className="text-muted-foreground text-sm">
                  {latestRun.run.suggestions.improvedDefinition}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
